import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaRocket, FaExchangeAlt } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useTheme } from '../../context/ThemeContext';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchPlans,
  fetchCurrentSubscription,
  createCheckoutSession,
  changePlan,
} from '../../features/subscriptions/subscriptionsSlice';
import { SubscriptionPlan } from '../../features/subscriptions/subscriptionsService';

const PricingPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const dispatch = useAppDispatch();
  const { plans, currentSubscription, isLoading, error } = useAppSelector((state) => state.subscriptions);
  const [changingPlanId, setChangingPlanId] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchPlans());
    dispatch(fetchCurrentSubscription());
  }, [dispatch]);

  const hasActiveSubscription =
    currentSubscription &&
    ['active', 'trialing'].includes(currentSubscription.status) &&
    currentSubscription.stripe_subscription_id;

  const handleSubscribe = async (planId: number) => {
    const result = await dispatch(createCheckoutSession(planId));
    if (createCheckoutSession.fulfilled.match(result)) {
      window.location.href = result.payload.checkout_url;
    }
  };

  const handleChangePlan = async (planId: number) => {
    const targetPlan = plans.find((p) => p.id === planId);
    if (!targetPlan) return;

    const result = await Swal.fire({
      title: t('change_plan', 'Change Plan'),
      text: t(
        'confirm_change_plan',
        'Your plan will be changed to {{plan}}. The price difference will be prorated.',
        { plan: targetPlan.name },
      ),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: t('confirm_change_plan_btn', 'Yes, Change Plan'),
      cancelButtonText: t('cancel', 'Cancel'),
      background: isDark ? '#1e1e2e' : '#fff',
      color: isDark ? '#e2e8f0' : '#1e293b',
      confirmButtonColor: '#8b5cf6',
    });

    if (!result.isConfirmed) return;

    setChangingPlanId(planId);
    try {
      const changeResult = await dispatch(changePlan(planId));
      if (changePlan.fulfilled.match(changeResult)) {
        Swal.fire({
          icon: 'success',
          title: t('success', 'Success'),
          text: t('plan_changed_success', 'Your plan has been changed successfully!'),
          timer: 2500,
          showConfirmButton: false,
          background: isDark ? '#1e1e2e' : '#fff',
          color: isDark ? '#e2e8f0' : '#1e293b',
        });
        dispatch(fetchCurrentSubscription());
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error', 'Error'),
          text: (changeResult.payload as string) || t('plan_change_failed', 'Failed to change plan.'),
          background: isDark ? '#1e1e2e' : '#fff',
          color: isDark ? '#e2e8f0' : '#1e293b',
        });
      }
    } finally {
      setChangingPlanId(null);
    }
  };

  const isCurrentPlan = (plan: SubscriptionPlan) => {
    return (
      currentSubscription?.plan_id === plan.id &&
      ['active', 'trialing'].includes(currentSubscription?.status || '')
    );
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(price);
  };

  if (isLoading && plans.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <Spinner animation="border" style={{ color: 'var(--brand-primary)' }} />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" style={{ padding: '1.5rem' }}>
      <div className="text-center mb-5">
        <h2 className="fw-bold" style={{ color: 'var(--text-primary)' }}>
          {t('pricing_title', 'Choose Your Plan')}
        </h2>
        <p style={{ color: 'var(--text-muted)' }}>
          {hasActiveSubscription
            ? t('pricing_subtitle_change', 'Select a different plan to change your subscription')
            : t('pricing_subtitle', 'Select the plan that best fits your needs')}
        </p>
      </div>

      {error && <Alert variant="warning" className="mb-4">{error}</Alert>}

      <Row className="g-4 justify-content-center">
        {plans.map((plan, index) => {
          const isCurrent = isCurrentPlan(plan);
          const canChange = hasActiveSubscription && !isCurrent;
          const isPopular = index === 1 && plans.length > 1;

          return (
            <Col key={plan.id} md={6} lg={4}>
              <div
                className="h-100 d-flex flex-column"
                style={{
                  background: 'var(--card-bg)',
                  border: isCurrent
                    ? '2px solid #22c55e'
                    : isPopular
                      ? '2px solid var(--brand-primary)'
                      : '1px solid var(--border-color)',
                  borderRadius: 20,
                  boxShadow: isPopular ? 'var(--shadow-brand)' : 'var(--shadow-sm)',
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                }}
              >
                <div className="d-flex flex-column flex-grow-1" style={{ padding: '2rem' }}>
                  {isCurrent && (
                    <div
                      style={{
                        background: '#22c55e',
                        color: '#fff',
                        textAlign: 'center',
                        padding: '8px 0',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        borderRadius: 12,
                        marginBottom: '1rem',
                      }}
                    >
                      <FaCheck className="me-1" /> {t('current_plan', 'Current Plan')}
                    </div>
                  )}
                  <div className="text-center mb-4">
                    <h4 className="fw-bold" style={{ color: 'var(--text-primary)' }}>
                      {plan.name}
                    </h4>
                    {plan.description && (
                      <p className="small" style={{ color: 'var(--text-muted)' }}>
                        {plan.description}
                      </p>
                    )}
                    <div className="my-3">
                      <span className="display-5 fw-bold" style={{ color: 'var(--text-primary)' }}>
                        {formatPrice(plan.price, plan.currency)}
                      </span>
                      <span style={{ color: 'var(--text-muted)' }}>
                        /{plan.interval_count > 1 ? `${plan.interval_count} ` : ''}
                        {t(`interval_${plan.interval}`, plan.interval)}
                      </span>
                    </div>
                    {plan.trial_days > 0 && (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: 20,
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          background: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)',
                          color: '#3b82f6',
                        }}
                      >
                        {t('trial_days', '{{days}} day free trial', { days: plan.trial_days })}
                      </span>
                    )}
                  </div>

                  <ul className="list-unstyled mb-4 flex-grow-1">
                    {plan.features?.map((feature, idx) => (
                      <li key={idx} className="mb-2 d-flex align-items-start">
                        <span
                          className="me-2 mt-1 flex-shrink-0 d-flex align-items-center justify-content-center"
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)',
                            color: '#22c55e',
                            fontSize: '0.7rem',
                          }}
                        >
                          <FaCheck />
                        </span>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                          {typeof feature === 'string'
                            ? feature
                            : feature.label || feature.name || JSON.stringify(feature)}
                        </span>
                      </li>
                    ))}
                    {plan.max_chatbots && (
                      <li className="mb-2 d-flex align-items-start">
                        <span
                          className="me-2 mt-1 flex-shrink-0 d-flex align-items-center justify-content-center"
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)',
                            color: '#22c55e',
                            fontSize: '0.7rem',
                          }}
                        >
                          <FaCheck />
                        </span>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                          {t('max_chatbots_label', 'Up to {{count}} chatbots', { count: plan.max_chatbots })}
                        </span>
                      </li>
                    )}
                    {plan.max_messages_per_month && (
                      <li className="mb-2 d-flex align-items-start">
                        <span
                          className="me-2 mt-1 flex-shrink-0 d-flex align-items-center justify-content-center"
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)',
                            color: '#22c55e',
                            fontSize: '0.7rem',
                          }}
                        >
                          <FaCheck />
                        </span>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                          {t('max_messages_label', '{{count}} messages/month', {
                            count: plan.max_messages_per_month,
                          })}
                        </span>
                      </li>
                    )}
                    {plan.max_knowledge_sources && (
                      <li className="mb-2 d-flex align-items-start">
                        <span
                          className="me-2 mt-1 flex-shrink-0 d-flex align-items-center justify-content-center"
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)',
                            color: '#22c55e',
                            fontSize: '0.7rem',
                          }}
                        >
                          <FaCheck />
                        </span>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                          {t('max_knowledge_label', '{{count}} knowledge sources', {
                            count: plan.max_knowledge_sources,
                          })}
                        </span>
                      </li>
                    )}
                  </ul>

                  <div className="text-center mt-auto">
                    {isCurrent ? (
                      <Button variant="outline-success" disabled className="w-100 btn-modern">
                        <FaCheck className="me-1" /> {t('current_plan', 'Current Plan')}
                      </Button>
                    ) : canChange ? (
                      <Button
                        variant="primary"
                        className="w-100 btn-modern btn-primary"
                        onClick={() => handleChangePlan(plan.id)}
                        disabled={changingPlanId === plan.id || isLoading}
                      >
                        {changingPlanId === plan.id ? (
                          <Spinner animation="border" size="sm" className="me-1" />
                        ) : (
                          <FaExchangeAlt className="me-1" />
                        )}
                        {t('select_plan_btn', 'Select Plan')}
                      </Button>
                    ) : (
                      <Button
                        variant={isPopular ? 'primary' : 'outline-secondary'}
                        className={`w-100 btn-modern ${isPopular ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={isLoading}
                      >
                        <FaRocket className="me-1" />
                        {isLoading ? t('loading', 'Loading...') : t('subscribe', 'Subscribe')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default PricingPage;
